import gamaGlobe from "@/assets/3D/gamaGlobe.glb";
import gamaGlobeTexture from "@/assets/3D/gamaGlobe-texture-4K.jpg";

import {
    Group, MathUtils, Vector3, RingGeometry, MeshBasicMaterial,
    Mesh, TextureLoader, SRGBColorSpace, LinearFilter, MeshToonMaterial, Quaternion
} from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js";

// 模組層 singleton：DRACOLoader 初始化會 fetch wasm decoder，
// 不共用會讓每個 manager 實例重新下載/建實例。
// 不設 decoderConfig.type → three 自動選 wasm（public/draco/ 同時放 js + wasm）。
const dracoLoader = new DRACOLoader().setDecoderPath( "/draco/" );
const gltfLoader = new GLTFLoader().setDRACOLoader( dracoLoader );
const sharedTextureLoader = new TextureLoader();

export default class PlanetsModelsManager {
    constructor ( scene ) {
        this.scene = scene;

        // Create a group for all orbiting planets
        this.orbitGroup = new Group();
        this.gamaGroup = new Group();
        this.gamaGroup.rotation.x = MathUtils.degToRad( 45 );
        this.gamaGroup.rotation.y = MathUtils.degToRad( 60 );

        // Add the orbit group to the scene
        this.scene.add( this.orbitGroup );
        this.scene.add( this.gamaGroup );

        this.addRipples();
        this.rotationConfig = {
            x: 8,
            z: 12,
        };
        // Tilt the orbit group
        this.orbitGroup.rotation.z = MathUtils.degToRad(
            this.rotationConfig.z,
        );
        this.orbitGroup.rotation.x = MathUtils.degToRad(
            this.rotationConfig.x,
        );
        // Create rotation axis
        this.orbitAxis = new Vector3( 0, 1, 0 )
            .applyAxisAngle(
                new Vector3( 0, 0, 1 ),
                MathUtils.degToRad( this.rotationConfig.z ),
            )
            .applyAxisAngle(
                new Vector3( 1, 0, 0 ),
                MathUtils.degToRad( this.rotationConfig.x ),
            );

        // Orbital radius and positions
        const orbitRadius = 2.3; // Distance from center
        // Distribution angles for planets around the orbit
        const orbitAngles = {
            earth: Math.PI * 1.666, // 0 degrees
            yellow: Math.PI * 0, // 72 degrees
            blueRing: Math.PI * 0.333, // 144 degrees
            red: Math.PI * 0.666, // 216 degrees
            greenRing: Math.PI * 0.999, // 288 degrees
            green: Math.PI * 1.333, // Full circle
        };

        this.models = {
            gama: null,
            blueRing: null,
            green: null,
            greenRing: null,
            red: null,
            yellow: null,
            earth: null,
        };

        // Define model paths and positions
        this.modelConfigs = {
            gama: {
                // path: "/about/glb/gama_go_for.glb",
                // path: "/3D/gamaGlobe.glb",
                path: gamaGlobe,
                position: new Vector3( 0, 0, 0 ),
                isApplyAoMap: true,
                isMain: true,
            },
            blueRing: {
                path: "/about/glb/blue_ring.glb",
                position: new Vector3(
                    orbitRadius * Math.cos( orbitAngles.blueRing ),
                    0,
                    orbitRadius * Math.sin( orbitAngles.blueRing ),
                ),
            },
            green: {
                path: "/about/glb/green_pic.glb",
                position: new Vector3(
                    orbitRadius * Math.cos( orbitAngles.green ),
                    0,
                    orbitRadius * Math.sin( orbitAngles.green ),
                ),
            },
            greenRing: {
                path: "/about/glb/green_ring.glb",
                position: new Vector3(
                    orbitRadius * Math.cos( orbitAngles.greenRing ),
                    0,
                    orbitRadius * Math.sin( orbitAngles.greenRing ),
                ),
            },
            red: {
                path: "/about/glb/red_pic.glb",
                position: new Vector3(
                    orbitRadius * Math.cos( orbitAngles.red ),
                    0,
                    orbitRadius * Math.sin( orbitAngles.red ),
                ),
            },
            yellow: {
                path: "/about/glb/yellow_pic.glb",
                position: new Vector3(
                    orbitRadius * Math.cos( orbitAngles.yellow ),
                    0,
                    orbitRadius * Math.sin( orbitAngles.yellow ),
                ),
            },
            earth: {
                path: "/about/glb/earth.glb",
                position: new Vector3(
                    orbitRadius * Math.cos( orbitAngles.earth ),
                    0,
                    orbitRadius * Math.sin( orbitAngles.earth ),
                ),
            },
        };

        this._orbitQuaternion = new Quaternion();
    }
    applyAoMapToModel ( model ) {
        if ( !this.aoMap ) return;

        model.traverse( ( child ) => {
            if ( child.isMesh && child.material ) {
                // We need to make sure the object has UV2 coordinates for aoMap
                if ( !child.geometry.hasAttribute( "uv2" ) ) {
                    child.geometry.setAttribute(
                        "uv2",
                        child.geometry.getAttribute( "uv" ),
                    );
                }

                child.material.aoMap = this.aoMap;
                child.material.aoMapIntensity = 1; // You can adjust strength here
                child.material.needsUpdate = true;
            }
        } );
    }
    applyMaterialToModel ( model, isMainModel ) {
        if ( isMainModel ) {
            // 回傳 Promise 讓 caller 可以延到 texture 解碼完才把模型 add 進 scene，
            // 避免使用者看到一瞬間白球再貼圖的閃爍。
            return new Promise( ( resolve ) => {
                sharedTextureLoader.load( gamaGlobeTexture, ( texture ) => {
                    texture.colorSpace = SRGBColorSpace;
                    texture.minFilter = LinearFilter;
                    texture.magFilter = LinearFilter;
                    texture.generateMipmaps = false;
                    texture.flipY = true;
                    model.children[ 0 ].material = new MeshBasicMaterial( {
                        map: texture,
                    } );
                    resolve();
                }, undefined, () => resolve() );
            } );
        }
        model.traverse( ( child ) => {
            if ( child.isMesh ) {
                const originalMaterial = child.material;
                const toonMaterial = new MeshToonMaterial( {
                    color: originalMaterial.color,
                    opacity: originalMaterial?.opacity || 1.0,
                } );
                // 原 mesh 若沒貼圖，別把 undefined 塞進 constructor，
                // 否則 THREE.Material.setValues 會 warn。
                if ( originalMaterial.map ) toonMaterial.map = originalMaterial.map;
                child.material = toonMaterial;
            }
        } );
        return Promise.resolve();
    }
    addGlobeToOrbitGroup ( globe ) {
        this.orbitGroup.add( globe );
    }
    addRipples () {
        const ringGeometry = new RingGeometry( 2.1, 2.12, 128 );
        const material1 = new MeshBasicMaterial( {
            color: 0xff6501,
            transparent: true,
        } );
        const material2 = new MeshBasicMaterial( {
            color: 0xff6501,
            transparent: true,
        } );
        const ripple1 = new Mesh( ringGeometry, material1 );
        const ripple2 = new Mesh( ringGeometry, material2 );
        this.ripples = [ ripple1, ripple2 ];
        this.ripplesGroup = new Group();
        this.ripples.forEach( ( ripple ) => {
            this.ripplesGroup.add( ripple );
        } );
        this.scene.add( this.ripplesGroup );
    }
    async loadAoMap () {
        const texture = await new Promise( ( resolve ) => {
            sharedTextureLoader.load( "/about/glb/ao/mask.jpg", ( texture ) => {
                resolve( texture );
            } );
        } );

        this.aoMap = texture;
        return texture;
    }
    async loadModels () {
        const loadPromises = Object.entries( this.modelConfigs ).map(
            ( [ name, config ] ) => {
                return new Promise( ( resolve, reject ) => {
                    gltfLoader.load(
                        config.path,
                        async ( gltf ) => {
                            try {
                                const model = gltf.scene;
                                model.position.copy( config.position );

                                if ( config.isApplyAoMap === true ) {
                                    this.applyAoMapToModel( model );
                                }
                                if ( config.isMain === true ) {
                                    model.scale.set( 1.5, 1.5, 1.5 );
                                    model.rotation.set(
                                        MathUtils.degToRad( -90 ),
                                        MathUtils.degToRad( 17.5 ),
                                        MathUtils.degToRad( 0 ),
                                    );
                                } else {
                                    model.scale.set( 0.48, 0.48, 0.48 );
                                }
                                // 等 texture 解完再 add → 避免主球白閃一幀
                                await this.applyMaterialToModel( model, config.isMain );

                                this.models[ name ] = model;
                                if ( name === "gama" ) {
                                    this.models.gama.name = "aboutGamaGlobe";
                                }
                                if ( !config.isMain ) {
                                    this.orbitGroup?.add( model );
                                } else {
                                    this.gamaGroup?.add( model );
                                }
                                resolve( model );
                            } catch ( err ) {
                                reject( err );
                            }
                        },
                        undefined,
                        ( error ) => reject( error ),
                    );
                } );
            },
        );

        // allSettled：單一 GLB 失敗不阻斷其他行星顯示與後續 gsap timeline 初始化
        await Promise.allSettled( loadPromises );
    }

    animate ( deltaTime, elapsed, landscapesProgress ) {
        if ( this.ripples ) {
            this.ripples.map( ( ripple, idx ) => {
                // Calculate progress (0 to 1) using modulo to create a loop
                const progress = Math.sin(
                    ( 1.5 * ( elapsed * 0.2 - idx * 0.2 ) ) % ( Math.PI / 2 ),
                );

                // Scale from 0 to 1
                const scale = progress;
                const threshold = 0.7;
                // Opacity from 1 to 0
                const opacity =
                    1 -
                    ( progress < threshold
                        ? 0
                        : ( progress - threshold ) / ( 1 - threshold ) );
                // Apply scale
                ripple.scale.set( scale, scale, scale );
                // Apply opacity
                if ( ripple.material ) {
                    ripple.material.opacity = opacity;
                }
            } );
        }

        // Rotate each satellite planet around its own axis
        if ( this.models.blueRing )
            this.models.blueRing.rotation.y += 0.3 * deltaTime;
        if ( this.models.green ) this.models.green.rotation.z += 0.1 * deltaTime;
        if ( this.models.greenRing )
            this.models.greenRing.rotation.y += 0.2 * deltaTime;
        if ( this.models.red ) this.models.red.rotation.y += 0.25 * deltaTime;
        if ( this.models.yellow )
            this.models.yellow.rotation.z += 0.2 * deltaTime;
        if ( this.models.earth ) this.models.earth.rotation.y += 0.8 * deltaTime;

        // Slowly rotate the entire orbit group around the center
        const scrollRotationTotalAngle = ( Math.PI * 2 * 5 ) / 6;
        const rotationAngle = landscapesProgress * scrollRotationTotalAngle;
        this.orbitGroup.quaternion.identity();
        this.orbitGroup.rotation.z = MathUtils.degToRad(
            this.rotationConfig.z,
        );
        this.orbitGroup.rotation.x = MathUtils.degToRad(
            this.rotationConfig.x,
        );

        this._orbitQuaternion.setFromAxisAngle( this.orbitAxis, rotationAngle );

        this.orbitGroup.quaternion.premultiply( this._orbitQuaternion );

        // Rotate the gama model independently
        this.gamaGroup.rotation.y += 0.2 * deltaTime;
    }

    animateDisappear ( progress ) {
        const scale = 1 - progress;
        if ( this.orbitGroup.children ) {
            this.orbitGroup.scale.set( scale, scale, scale );
        }
        if ( this.ripplesGroup ) {
            this.ripplesGroup.scale.set( scale, scale, scale );
        }
    }

    getModel ( name ) {
        return this.models[ name ];
    }

    destroy () {
        // console.log("Destroying PlanetsModelsManager resources...");

        // Dispose of all models (geometry, material, textures)
        Object.values( this.models ).forEach( ( model ) => {
            if ( model ) {
                model.traverse( ( child ) => {
                    if ( child.isMesh ) {
                        // console.log("isMesh Works");
                        if ( child.geometry ) {
                            child.geometry.dispose();
                            // console.log(
                            //     `Disposed geometry for model child: ${child.name || child.uuid}`,
                            // );
                        }
                        if ( child.material ) {
                            if ( Array.isArray( child.material ) ) {
                                child.material.forEach( ( material ) => {
                                    if ( material.map ) material.map.dispose();
                                    if ( material.aoMap )
                                        material.aoMap.dispose(); // If you applied aoMap directly to sub-materials
                                    // Dispose other maps if any (normalMap, emissiveMap, etc.)
                                    material.dispose();
                                    // console.log(`Disposed material (array item) for model child: ${material.uuid}`);
                                } );
                            } else {
                                if ( child.material.map )
                                    child.material.map.dispose();
                                if ( child.material.aoMap )
                                    child.material.aoMap.dispose();
                                // Dispose other maps if any
                                child.material.dispose();
                                // console.log(`Disposed material (single) for model child: ${child.material.uuid}`);
                            }
                        }
                    }
                } );
                // After cleaning children, remove the model itself from its parent (orbitGroup or gamaGroup)
                if ( model.parent ) {
                    model.parent.remove( model );
                    // console.log(`Removed model from parent: ${model.name || model.uuid}`);
                }
            }
        } );
        this.models = {}; // Clear references to models

        // Dispose of ripples
        if ( this.ripplesGroup ) {
            this.ripplesGroup.traverse( ( child ) => {
                if ( child.isMesh ) {
                    if ( child.geometry ) child.geometry.dispose();
                    if ( child.material ) {
                        // Assuming ripple material is simple MeshBasicMaterial
                        if ( Array.isArray( child.material ) ) {
                            child.material.forEach( ( mat ) => mat.dispose() );
                        } else {
                            child.material.dispose();
                        }
                    }
                }
            } );
            if ( this.ripplesGroup.parent ) {
                this.ripplesGroup.parent.remove( this.ripplesGroup );
                // console.log("Removed ripplesGroup from parent.");
            }
        }
        this.ripples = []; // Clear array
        this.ripplesGroup = null; // Clear reference

        // Remove main groups from the scene (if they were added to the scene directly)
        // These checks are important because removeFromParent might have already been called or parent might be null
        if ( this.orbitGroup ) {
            if ( this.orbitGroup.parent ) {
                this.orbitGroup.parent.remove( this.orbitGroup );
                // console.log("Removed orbitGroup from parent.");
            }
            this.orbitGroup = null; // Clear reference
        }

        if ( this.gamaGroup ) {
            if ( this.gamaGroup.parent ) {
                this.gamaGroup.parent.remove( this.gamaGroup );
                // console.log("Removed gamaGroup from parent.");
            }
            this.gamaGroup = null; // Clear reference
        }

        // Dispose of loaded AO map texture if it was loaded by this manager
        if ( this.aoMap ) {
            this.aoMap.dispose();
            this.aoMap = null;
            // console.log("Disposed aoMap texture.");
        }

        // It's good practice to nullify the scene reference if this manager is truly done
        // However, this class receives the scene in constructor, it doesn't own it.
        // So, it should only remove things it added TO the scene.
        // this.scene = null; // Generally, don't nullify things passed in constructor unless manager owns them.

        // console.log("PlanetsModelsManager resources destroyed and cleaned up.");
    }
}
